import {
  useCallback, useEffect, useState,
} from 'react';
import { fetchFeeQuote } from '../services/fee-quote';
import { hasCompleteFeeInputs, isAutismAssessment, toMedicareType } from '../utils/fee';
import type { FeeQuote, FeeSelection } from '../utils/fee';

export type FeeQuoteStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseFeeQuoteReturn {
  quote: FeeQuote | null;
  status: FeeQuoteStatus;
  /** Asks for the quote again, e.g. after a failure or FEE_CHANGED. */
  reload: () => void;
}

/**
 * Fetches the server fee once the patient's state, card type and every fee
 * answer are known. Stays idle (no request) until then, and clears the
 * previous quote whenever an answer changes or the quote is reloaded.
 */
const useFeeQuote = (
  state: string,
  medicareTypeAnswer: string,
  selection: FeeSelection,
): UseFeeQuoteReturn => {
  const medicareType = toMedicareType(medicareTypeAnswer);
  const { location, feeAssessmentScope, seenPsychiatrist } = selection;
  // Autism skips the prescribing question, so a leftover answer must not refetch.
  const prescriptionType = isAutismAssessment(feeAssessmentScope)
    ? ''
    : selection.prescriptionType;
  const canQuote = Boolean(state && medicareType) && hasCompleteFeeInputs({
    location, feeAssessmentScope, prescriptionType, seenPsychiatrist,
  });

  const [quote, setQuote] = useState<FeeQuote | null>(null);
  const [status, setStatus] = useState<FeeQuoteStatus>('idle');
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    setQuote(null);
    if (!canQuote || !medicareType) {
      setStatus('idle');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');
    fetchFeeQuote(state, medicareType, {
      location, feeAssessmentScope, prescriptionType, seenPsychiatrist,
    })
      .then((result) => {
        if (cancelled) return;
        setQuote(result);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [
    canQuote, state, medicareType, location, feeAssessmentScope,
    prescriptionType, seenPsychiatrist, reloadCount,
  ]);

  const reload = useCallback(() => setReloadCount((count) => count + 1), []);

  return { quote, status, reload };
};

export default useFeeQuote;
